import { clusterPins } from '@fsv/core';
import type { Point } from './zoom';

export interface ScreenPin<T> {
  id: string;
  /** Screen position relative to the board's top-left corner, in CSS pixels. */
  screen: Point;
  item: T;
}

export interface ScreenCluster<T> {
  key: string;
  center: Point;
  members: ScreenPin<T>[];
}

/** Two visible dots closer than this (CSS px) would overlap their 44 px tap targets. */
export const CLUSTER_DISTANCE_PX = 30;

/**
 * Clusters pins by their ON-SCREEN distance, so zooming in splits a cluster.
 *
 * Reuses core's clusterPins: screen pixels divided by the board width are a
 * coordinate system where one unit is the same length on both axes, so the
 * aspect is 1 and the threshold is px / boardWidth.
 */
export function clusterOnScreen<T>(
  pins: readonly ScreenPin<T>[],
  boardWidth: number,
  thresholdPx = CLUSTER_DISTANCE_PX,
): ScreenCluster<T>[] {
  if (boardWidth <= 0) return [];
  const byId = new Map(pins.map((p) => [p.id, p]));
  const positioned = pins.map((p) => ({ id: p.id, x: p.screen.x / boardWidth, y: p.screen.y / boardWidth }));
  return clusterPins(positioned, thresholdPx / boardWidth, 1).map((group) => {
    const members = group.map((g) => byId.get(g.id)).filter((p): p is ScreenPin<T> => p !== undefined);
    const center = {
      x: members.reduce((sum, m) => sum + m.screen.x, 0) / members.length,
      y: members.reduce((sum, m) => sum + m.screen.y, 0) / members.length,
    };
    return { key: members.map((m) => m.id).sort().join('+'), center, members };
  });
}
